import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import DatePickerModal from '../shared/DatePickerModal';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { createSprint, updateSprint, fetchSprintVersions, fetchSprintDetails, createSprintPriority, updateTicketDropdown, deleteTicketDropdown, fetchSprintSettings, fetchTicketDropdowns } from '../store';
import Check from '@mui/icons-material/Check';
import Clear from '@mui/icons-material/Clear';
import Flag from '@mui/icons-material/Flag';
import Edit from '@mui/icons-material/Edit';
import { CHARACTER_LIMITS, SPRINT_STATUSES } from '../shared/appConstants';
import { REGEX } from '../shared/regex';
import Delete from '@mui/icons-material/Delete';
import { getPriorityColor } from '../shared/commonFunctions';
import CustomColorPopover from '../view/shared/CustomColorPopover';
import { COLORS } from '../shared/colors';
import './SprintModal.scss';


const initialForm = {
  name: '',
  goal: '',
  priority: '',
  version: '',
  startDate: '',
  endDate: ''
};

const SprintModal = ({ open, onClose, projectId, sprintData, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { loading, sprintVersions, sprintSettings } = useSelector(state => state.scrum || {});
  const { dropdowns } = useSelector(state => state.tickets || {});
  const sprintPriorities = dropdowns?.sprint_priorities || [];
  
  const [isCreatingPriority, setIsCreatingPriority] = useState(false);
  const [newPriorityName, setNewPriorityName] = useState('');
  const [newPriorityColor, setNewPriorityColor] = useState(COLORS.primary);
  const [createPriorityAnchor, setCreatePriorityAnchor] = useState(null);
  const [editPriorityAnchor, setEditPriorityAnchor] = useState(null);
  const [isCreatingPrioritySubmitting, setIsCreatingPrioritySubmitting] = useState(false);
  const [isPrioritySelectOpen, setIsPrioritySelectOpen] = useState(false);
  const [editingPriority, setEditingPriority] = useState(null);
  const [startDateAnchorEl, setStartDateAnchorEl] = useState(null);

  const disabledDates = useMemo(() => {
    const dates = new Set();
    const sprints = dropdowns?.scrum_sprints || [];
    sprints.forEach(sprint => {
      // Exclude the current sprint from being disabled, if editing
      if (sprintData && String(sprint.id) === String(sprintData.id)) return;
      if (sprint.start_date && sprint.end_date) {
        const start = new Date(sprint.start_date);
        const end = new Date(sprint.end_date);
        let curr = new Date(start);
        while (curr <= end) {
          const year = curr.getFullYear();
          const month = String(curr.getMonth() + 1).padStart(2, '0');
          const day = String(curr.getDate()).padStart(2, '0');
          dates.add(`${year}-${month}-${day}`);
          curr.setDate(curr.getDate() + 1);
        }
      }
    });
    return Array.from(dates);
  }, [dropdowns?.scrum_sprints, sprintData]);

  const PRIORITY_NAME_LIMIT = 15;

  const handleUpdatePriority = async () => {
    if (editingPriority?.newName?.trim()) {
      try {
        await dispatch(updateTicketDropdown({ 
          id: editingPriority.id, 
          name: editingPriority.newName.trim(), 
          colour: editingPriority.newColor,
          category: 'sprint_priority',
          projectId
        })).unwrap();
        // toast handled in thunk
      } catch (error) {
        // toast handled in thunk
      } finally {
        setEditingPriority(null);
      }
    }
  };

  const handleDeletePriority = async (priority) => {
    try {
      await dispatch(deleteTicketDropdown({ id: priority.id, category: 'sprint_priority' })).unwrap();
      if (formData.priority === priority.id) {
        setFormData(prev => ({ ...prev, priority: '' }));
      }
      // toast handled in thunk
    } catch (error) {
      // toast handled in thunk
    }
  };

  const handleCreatePriorityConfirm = async () => {
    if (!newPriorityName.trim() || !projectId) return;
    setIsCreatingPrioritySubmitting(true);
    try {
      const created = await dispatch(createSprintPriority({ name: newPriorityName, colour: newPriorityColor, projectId })).unwrap();
      if (created?.id) {
        setFormData(prev => ({ ...prev, priority: created.id }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCreatingPrioritySubmitting(false);
      setNewPriorityName('');
      setNewPriorityColor(COLORS.primary);
      setIsCreatingPriority(false);
      setIsPrioritySelectOpen(false);
    }
  };

  const [formData, setFormData] = useState(initialForm);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoadingSprintData, setIsLoadingSprintData] = useState(false);
  const [originalVersion, setOriginalVersion] = useState('');

  // Reset form when opened
  useEffect(() => {
    if (open) {
      if (sprintData) {
        setOriginalVersion(sprintData.version || '');
        const initialSprintForm = {
          name: sprintData.name || '',
          goal: sprintData.goal || '',
          priority: sprintData.priority || '',
          version: sprintData.version || '',
          startDate: sprintData.start_date ? sprintData.start_date.split('T')[0] : '',
          endDate: sprintData.end_date ? sprintData.end_date.split('T')[0] : ''
        };
        setFormData(initialSprintForm);
        setOriginalFormData(initialSprintForm);

        if (sprintData.id) {
          setIsLoadingSprintData(true);
          dispatch(fetchSprintDetails(sprintData.id)).unwrap().then((response) => {
             const fullDetails = response?.data || response;
             if (fullDetails) {
                 const fetchedVersion = fullDetails.version || initialSprintForm.version;
                 setOriginalVersion(fetchedVersion);
                 
                 const updatedForm = {
                     name: fullDetails.name || initialSprintForm.name,
                     goal: fullDetails.goal || initialSprintForm.goal,
                     priority: fullDetails.priority?.id || fullDetails.priority || initialSprintForm.priority,
                     version: fetchedVersion,
                     startDate: fullDetails.start_date ? fullDetails.start_date.split('T')[0] : initialSprintForm.startDate,
                     endDate: fullDetails.end_date ? fullDetails.end_date.split('T')[0] : initialSprintForm.endDate
                 };
                 setFormData(prev => ({ ...prev, ...updatedForm }));
                 setOriginalFormData(updatedForm);
             }
          }).catch(console.error).finally(() => {
             setIsLoadingSprintData(false);
          });
        }
      } else {
        setOriginalVersion('');
        setFormData(initialForm);
        setOriginalFormData(null);
      }
      setErrors({});
    }
  }, [open, sprintData, dispatch]);

  // Fetch sprint versions when opening for a specific project
  useEffect(() => {
    if (open && projectId) {
      dispatch(fetchTicketDropdowns(projectId));
      dispatch(fetchSprintVersions(projectId));
      dispatch(fetchSprintSettings());
    }
  }, [open, projectId, dispatch]);

  // Auto-fill logic
  useEffect(() => {
    if (open && !sprintData && sprintVersions && sprintVersions.length > 0) {
      // Sort versions naturally (so v10 comes after v2) and get the highest one
      const sortedVersions = [...sprintVersions].sort((a, b) => 
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
      
      const highestVersion = sortedVersions[sortedVersions.length - 1];
      const match = highestVersion?.match(/^(.*?)(\d+)$/);
      
      if (match) {
        const prefix = match[1];
        const nextNumber = parseInt(match[2], 10) + 1;
        const nextVersion = `${prefix}${nextNumber}`;
        
        if (validateVersion(nextVersion) && !sprintVersions.includes(nextVersion)) {
           setFormData(prev => ({ ...prev, version: nextVersion }));
        } else {
           setFormData(prev => ({ ...prev, version: '' }));
        }
      }
    }
  }, [open, sprintData, sprintVersions]);

  // Alphanumeric validation including space, hyphen, underscore, period, comma
  const isValidFormat = (val) => REGEX.sprintText.test(val);

  const validateName = (value) => value.length <= CHARACTER_LIMITS.SCRUM_SPRINT_NAME && isValidFormat(value);
  const validateGoal = (value) => value.length <= CHARACTER_LIMITS.SCRUM_SPRINT_GOAL && isValidFormat(value);
  const validateVersion = (value) => value.length <= CHARACTER_LIMITS.SCRUM_SPRINT_VERSION && /^[a-zA-Z0-9\s\-_.]*$/.test(value);

  const validateDates = (start, end) => {
    if (!start || !end) return '';
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    
    // Ensure both dates are at midnight for accurate day difference
    startDateObj.setHours(0, 0, 0, 0);
    endDateObj.setHours(0, 0, 0, 0);

    if (endDateObj < startDateObj) {
      return 'End Date must be after Start Date.';
    }

    const diffTime = Math.abs(endDateObj - startDateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
    if (diffDays < 5) {
      return t('scrum.minGap5Days');
    }

    return '';
  };

  const handleInputChange = (e) => {
    let { name, value } = e.target;
    
    // Truncate overflow from the end if past character limits
    if (name === 'name' && value.length > CHARACTER_LIMITS.SCRUM_SPRINT_NAME) value = value.slice(0, CHARACTER_LIMITS.SCRUM_SPRINT_NAME);
    if (name === 'goal' && value.length > CHARACTER_LIMITS.SCRUM_SPRINT_GOAL) value = value.slice(0, CHARACTER_LIMITS.SCRUM_SPRINT_GOAL);
    if (name === 'version' && value.length > CHARACTER_LIMITS.SCRUM_SPRINT_VERSION) value = value.slice(0, CHARACTER_LIMITS.SCRUM_SPRINT_VERSION);

    // Character limits and alphanumeric checks
    if (name === 'name' && !validateName(value)) return;
    if (name === 'goal' && !validateGoal(value)) return;
    if (name === 'version' && !validateVersion(value)) return;

    const newFormData = { ...formData, [name]: value };

    // Date validations
    if (name === 'startDate' || name === 'endDate') {
      const newStartDate = name === 'startDate' ? value : formData.startDate;
      let newEndDate = name === 'endDate' ? value : formData.endDate;
      
      if (name === 'startDate') {
        const durationVal = sprintSettings?.json_value?.duration;
        const durationDays = durationVal ? parseInt(String(durationVal).replace(/\D/g, ''), 10) : 14;
        
        if (durationDays && !isNaN(durationDays) && newStartDate) {
          const start = new Date(newStartDate);
          start.setDate(start.getDate() + durationDays);
          
          const year = start.getFullYear();
          const month = String(start.getMonth() + 1).padStart(2, '0');
          const day = String(start.getDate()).padStart(2, '0');
          newEndDate = `${year}-${month}-${day}`;
          newFormData.endDate = newEndDate;
        }
      }
      
      const dateError = validateDates(newStartDate, newEndDate);
      setErrors(prev => ({ ...prev, dates: dateError }));
    }

    setFormData(newFormData);

    if (name === 'version') {
       if (sprintVersions && sprintVersions.includes(value) && value !== originalVersion) {
           setErrors(prev => ({ ...prev, version: t('scrum.versionAlreadyUsed', 'Version already used') }));
       } else {
           setErrors(prev => ({ ...prev, version: '' }));
       }
    }
  };

  const isFormValid = () => {
    const { name, goal, priority, version, startDate, endDate } = formData;
    return (
      name.trim().length > 0 &&
      goal.trim().length > 0 &&
      priority !== '' &&
      version.trim().length > 0 &&
      startDate !== '' &&
      endDate !== '' &&
      !errors.dates &&
      !errors.version
    );
  };

  const isFormChanged = () => {
    if (!sprintData || !originalFormData) return true; // Always changed if creating new
    return (
      formData.name.trim() !== originalFormData.name.trim() ||
      formData.goal.trim() !== originalFormData.goal.trim() ||
      formData.priority !== originalFormData.priority ||
      formData.version.trim() !== originalFormData.version.trim() ||
      formData.startDate !== originalFormData.startDate ||
      formData.endDate !== originalFormData.endDate
    );
  };

  const submitSprint = async (isStart) => {
    if (!isFormValid()) return;
    
    if (sprintVersions && sprintVersions.includes(formData.version.trim()) && formData.version.trim() !== originalVersion) {
        setErrors(prev => ({ ...prev, version: t('scrum.versionAlreadyUsed', 'Version already used') }));
        return;
    }

    if (!projectId && !sprintData) {
      toast.error(t('scrum.projectIdRequired'));
      return;
    }

    let payload = {};

    if (sprintData?.id && originalFormData) {
      if (formData.name.trim() !== originalFormData.name.trim()) payload.name = formData.name.trim();
      if (formData.goal.trim() !== originalFormData.goal.trim()) payload.goal = formData.goal.trim();
      if (formData.priority !== originalFormData.priority) payload.priority = formData.priority;
      if (formData.version.trim() !== originalFormData.version.trim()) payload.version = formData.version.trim();
      if (formData.startDate !== originalFormData.startDate) payload.start_date = formData.startDate;
      if (formData.endDate !== originalFormData.endDate) payload.end_date = formData.endDate;
    } else {
      payload = {
        name: formData.name.trim(),
        goal: formData.goal.trim(),
        priority: formData.priority,
        version: formData.version.trim(),
        start_date: formData.startDate,
        end_date: formData.endDate
      };
    }

    try {
      if (sprintData?.id) {
        if (Object.keys(payload).length > 0) {
          await dispatch(updateSprint({ sprintId: sprintData.id, data: payload })).unwrap();
          toast.success(t('scrum.updateSprintSuccess'));
        }
      } else {
        await dispatch(createSprint({ projectId, data: payload })).unwrap();
        toast.success(isStart ? t('scrum.startSprintSuccess') : t('scrum.createSprintSuccess'));
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast.error(error || t(sprintData?.id ? 'scrum.updateSprintFailed' : 'scrum.createSprintFailed'));
    }
  };

  const handleSave = () => submitSprint(false);

  // Get today's local date in YYYY-MM-DD for min attribute
  const getLocalDateString = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const today = getLocalDateString();

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      PaperProps={{
        style: { borderRadius: '8px', maxWidth: '700px' }
      }}
    >
      <DialogContent className="sprint-modal-container">
        
        {/* Header */}
        <Box className="sprint-modal-header">
          <h2>{sprintData ? t('scrum.editSprint') : t('scrum.createSprint')}</h2>
          <IconButton onClick={onClose} size="small" className="close-icon" disableRipple>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className="sprint-modal-separator-top" />

        {/* Form Fields */}
        {isLoadingSprintData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <CircularProgress />
          </Box>
        ) : (
        <Box className="sprint-modal-form">
          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label={t('scrum.sprintName')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              variant="outlined"
              required
            />
            <Typography className="character-count">{formData.name.length}/{CHARACTER_LIMITS.SCRUM_SPRINT_NAME}</Typography>
          </Box>

          <Box sx={{ width: '100%' }}>
            <TextField
              fullWidth
              label={t('scrum.sprintGoal')}
              name="goal"
              value={formData.goal}
              onChange={handleInputChange}
              variant="outlined"
              multiline
              rows={3}
              required
            />
            <Typography className="character-count">{formData.goal.length}/{CHARACTER_LIMITS.SCRUM_SPRINT_GOAL}</Typography>
          </Box>

          <Box className="form-row">
            <Box className="priority-select-container">
              <FormControl fullWidth required>
                <InputLabel>{t('scrum.priority')}</InputLabel>
                <Select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  label={t('scrum.priority')}
                  open={isPrioritySelectOpen}
                  onOpen={() => setIsPrioritySelectOpen(true)}
                  onClose={(e) => {
                    if (e && e.target && e.target.closest && e.target.closest('.create-option')) return;
                    if (isCreatingPriority) return;
                    setIsPrioritySelectOpen(false);
                    setIsCreatingPriority(false);
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 300 }
                    },
                    MenuListProps: {
                      autoFocusItem: false
                    }
                  }}
                  renderValue={(selected) => {
                    const priority = sprintPriorities.find(p => p.id === selected);
                    if (!priority) return null;
                    return (
                      <Box className="priority-render-value">
                        <Flag sx={{ color: getPriorityColor(priority, { priorities: sprintPriorities }) }} className="flag-icon" />
                        <Typography className="priority-text">{priority.name}</Typography>
                      </Box>
                    );
                  }}
                >
                  <Box
                    className="create-option priority-create-option"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      if (!isCreatingPriority) {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsCreatingPriority(true);
                      }
                    }}
                  >
                    {!isCreatingPriority ? (
                      <Box className="create-btn-content">
                        <AddIcon sx={{ fontSize: 16 }} />
                        <span>Create Priority</span>
                      </Box>
                    ) : (
                      <Box className="create-input-wrapper">
                        <TextField 
                          autoFocus
                          size="small"
                          placeholder={t('scrum.newPriorityName', 'New priority name')}
                          inputProps={{ maxLength: PRIORITY_NAME_LIMIT }}
                          helperText={`${newPriorityName.length}/${PRIORITY_NAME_LIMIT} characters`}
                          value={newPriorityName}
                          onChange={(e) => { e.stopPropagation(); setNewPriorityName(e.target.value); }}
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleCreatePriorityConfirm();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              setIsCreatingPriority(false);
                            }
                          }}
                          className="priority-textfield" 
                        />
                        <Box className="icon-actions">
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              setCreatePriorityAnchor(e.currentTarget);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="color-picker-preview"
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: newPriorityColor,
                              
                              mr: 1
                            }}
                          />
                          <CustomColorPopover
                            open={Boolean(createPriorityAnchor)}
                            anchorEl={createPriorityAnchor}
                            onClose={() => setCreatePriorityAnchor(null)}
                            colour={newPriorityColor}
                            onChange={setNewPriorityColor}
                          />
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); handleCreatePriorityConfirm(); }} 
                            disabled={isCreatingPrioritySubmitting || !newPriorityName.trim() || newPriorityName.length > PRIORITY_NAME_LIMIT} 
                            className="icon-success"
                          >
                            <Check sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); setIsCreatingPriority(false); setNewPriorityName(''); setNewPriorityColor(COLORS.primary); }} 
                            className="icon-error"
                          >
                            <Clear sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {sprintPriorities.map(p => (
                    <MenuItem key={p.id} value={p.id} onClick={(e) => {
                      if (editingPriority?.id === p.id) { e.preventDefault(); return; }
                      // Allow normal Select behavior if not editing
                    }} className="priority-dropdown-item">
                      {editingPriority?.id === p.id ? (
                        <Box className="edit-inline-wrapper" onClick={(e) => e.stopPropagation()}>
                          <TextField
                            size="small" autoFocus
                            value={editingPriority.newName}
                            onChange={(e) => { e.stopPropagation(); setEditingPriority(prev => ({ ...prev, newName: e.target.value })); }}
                            onKeyDown={(e) => { 
                              e.stopPropagation(); 
                              if (e.key === 'Enter') { e.preventDefault(); handleUpdatePriority(); } 
                              if (e.key === 'Escape') { e.preventDefault(); setEditingPriority(null); } 
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ maxLength: PRIORITY_NAME_LIMIT }}
                            helperText={`${editingPriority.newName.length}/${PRIORITY_NAME_LIMIT} characters`}
                            className="priority-textfield"
                          />
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditPriorityAnchor(e.currentTarget);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="color-picker-preview"
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: editingPriority.newColor,
                              
                              mr: 1
                            }}
                          />
                          <CustomColorPopover
                            open={Boolean(editPriorityAnchor)}
                            anchorEl={editPriorityAnchor}
                            onClose={() => setEditPriorityAnchor(null)}
                            colour={editingPriority.newColor}
                            onChange={(color) => setEditingPriority(prev => ({ ...prev, newColor: color }))}
                          />
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUpdatePriority(); }} disabled={!editingPriority.newName?.trim() || editingPriority.newName.length > PRIORITY_NAME_LIMIT} className="icon-success">
                            <Check sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingPriority(null); }} className="icon-error">
                            <Clear sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box className="read-mode-wrapper">
                          <Flag sx={{ color: getPriorityColor(p, { priorities: sprintPriorities }) }} className="flag-icon" />
                          <Typography className="text-truncate priority-text">
                            {p.name}
                          </Typography>
                          {!p.status && (
                            <Box className="action-icons-wrapper">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingPriority({ ...p, newName: p.name, newColor: p.colour || COLORS.primary });
                                }}
                                className="icon-action-small"
                              >
                                <Edit sx={{ fontSize: 14 }} />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  handleDeletePriority(p); 
                                }} 
                                className="icon-action-small"
                              >
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Autocomplete
                freeSolo
                options={sprintVersions || []}
                value={formData.version}
                onInputChange={(e, newValue) => handleInputChange({ target: { name: 'version', value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('scrum.version')}
                    variant="outlined"
                    required
                    error={!!errors.version}
                    helperText={errors.version}
                  />
                )}
              />
              <Typography className="character-count">{formData.version.length}/{CHARACTER_LIMITS.SCRUM_SPRINT_VERSION}</Typography>
            </Box>
          </Box>

          <Box className="form-row">
            <TextField
              fullWidth
              label={t('scrum.startDate')}
              name="startDate"
              type="text"
              value={formData.startDate ? new Date(formData.startDate).toLocaleDateString('en-US') : ''}
              onClick={(e) => {
                if (sprintData?.status?.toLowerCase() !== SPRINT_STATUSES.ACTIVE) {
                  setStartDateAnchorEl(e.currentTarget);
                }
              }}
              placeholder="dd/mm/yyyy"
              sx={{ '& .MuiInputBase-input::placeholder': { color: 'black', opacity: 1 } }}
              InputProps={{
                readOnly: true,
                className: sprintData?.status?.toLowerCase() !== SPRINT_STATUSES.ACTIVE ? 'is-clickable' : '',
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              disabled={sprintData?.status?.toLowerCase() === SPRINT_STATUSES.ACTIVE}
              required
              className="disabled-date-field"
            />
            {startDateAnchorEl && (
              <DatePickerModal
                open={Boolean(startDateAnchorEl)}
                anchorEl={startDateAnchorEl}
                onClose={() => setStartDateAnchorEl(null)}
                mode="dateRange"
                allowSingleSelection={true}
                autoApplySingleSelection={true}
                hideDueDate={true}
                hideCards={true}
                minDate={sprintData && formData.startDate < today ? formData.startDate : today}
                onDateChange={(dates) => {
                  if (dates && dates.length > 0) {
                    handleInputChange({ target: { name: 'startDate', value: dates[0] } });
                  }
                }}
                disabledDates={disabledDates}
              />
            )}

            <TextField
              fullWidth
              label={t('scrum.endDate')}
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              inputProps={{ min: formData.startDate || today }}
              error={!!errors.dates}
              helperText={errors.dates}
              disabled={true}
              required
              className="disabled-date-field"
            />
          </Box>
        </Box>
        )}

        {!isLoadingSprintData && (
          <>
            <Box className="sprint-modal-separator-bottom" />

            {/* Action Buttons */}
            <Box className="sprint-modal-actions">
              <Box className="left-actions">
                <Button className="btn-cancel" onClick={onClose} disableRipple>
                  {t('common.cancel')}
                </Button>
              </Box>
              <Box className="right-actions">

                <Button 
                  className="btn-save-sprint" 
                  onClick={handleSave}
                  disabled={!isFormValid() || !isFormChanged() || loading}
                  disableRipple
                >
                  {sprintData ? t('scrum.updateSprint') : t('scrum.saveSprint')}
                </Button>
              </Box>
            </Box>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default SprintModal;
